import {Bill, BillRestControllerApiFactory, BillSearchTotals, BillSummary} from "arteco-api-client-ts";
import AppList, {AppListConfig} from "../../../../components/layout/list";
import {useApiManager} from "../../../../components/utils/api";
import {useEffect, useState} from "react";
import {Box, Chip, Stack, TableCell, TextField, Typography} from "@mui/material";
import {useNavigate, useSearchParams} from "react-router-dom";
import {dateToString} from "../../../../components/utils/dates";
import {euros, SoftChip} from "../../../../components/kit";
import {billStatus, isBillOverdue} from "./labels";


interface BillListFilter {
	advanced: boolean;
	pending: boolean;
	overdue: boolean;
}

export const BillListPage = () => {
	const navigate = useNavigate();
	const apiManager = useApiManager();
	const [searchParams] = useSearchParams();

	const ctrl = apiManager.factory(BillRestControllerApiFactory);
	const budgetIdValue = searchParams.get("budgetId");
	const budgetId = budgetIdValue ? Number(budgetIdValue) : undefined;

	const [listFilter, setListFilter] = useState<BillListFilter>({
		advanced: false,
		pending: false,
		// Desde «Qué requiere atención» de la visión general se llega con ?overdue=1 (#14497).
		overdue: searchParams.get("overdue") === "1",
	});
	const [trigger, setTrigger] = useState<number>(0);
	const [summary, setSummary] = useState<BillSummary>({});
	// Intervalo de fecha de emisión; vacío = ese extremo no filtra (#13569).
	const [dateFrom, setDateFrom] = useState<string>("");
	const [dateTo, setDateTo] = useState<string>("");
	const [searchTotals, setSearchTotals] = useState<BillSearchTotals | null>(null);
	const [searching, setSearching] = useState<boolean>(false);

	useEffect(() => {
		apiManager
		.execute(ctrl.summaryBill())
		.then(summary => setSummary(summary))
	}, []);

	/** Los filtros se suman (pendientes y adelantadas a la vez); «Todas» los quita. */
	const toggle = (key: keyof BillListFilter) => {
		setListFilter({...listFilter, [key]: !listFilter[key]});
		setTrigger(trigger + 1);
	};
	const noFilter = !listFilter.pending && !listFilter.overdue && !listFilter.advanced;

	const qualifiers = [
		listFilter.pending ? "pendientes" : "",
		listFilter.overdue ? "vencidas" : "",
		listFilter.advanced ? "adelantadas" : "",
	].filter(Boolean);

	const listConfig: AppListConfig<Bill> = {
		title: "Facturas" + (qualifiers.length
			? " " + (qualifiers.length > 1 ? qualifiers.slice(0, -1).join(", ") + " y " + qualifiers[qualifiers.length - 1] : qualifiers[0])
			: "") + (budgetId ? ` del presupuesto ${budgetId}` : ""),
		onClick: "/app/admin/billing/bill",
		headers: [
			{title: "Número", width: "11%"},
			{title: "Estado", width: "10%"},
			{title: "Vencimiento", width: "11%"},
			{title: "Cliente", width: "14%"},
			{title: "Descripción", width: "30%"},
			{title: "Unidad", width: "10%"},
			{title: "Total con IVA", align: "right", width: "14%"},
		],
		decorator: (bean: Bill) => {
			const status = billStatus(bean);
			return (
				<>
					<TableCell sx={{whiteSpace: "nowrap"}}>
						<Typography variant="body2" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{bean.number}</Typography>
						<Typography variant="caption" color="text.secondary">{dateToString(bean.issueDate)}</Typography>
					</TableCell>
					<TableCell><SoftChip label={status.label} tone={status.tone}/></TableCell>
					<TableCell sx={{whiteSpace: "nowrap"}}>
						<Typography variant="body2" color={isBillOverdue(bean) ? "error.main" : "text.primary"}
									fontWeight={isBillOverdue(bean) ? 600 : 400}>
							{bean.dueDate ? dateToString(bean.dueDate) : "—"}
						</Typography>
						{bean.paymentMethod && bean.paymentMethod !== "TRANSFERENCIA" && (
							<Typography variant="caption" color="text.secondary">
								{bean.paymentMethod === "ADEUDO_SEPA" ? "SEPA" : "Confirming"}
							</Typography>
						)}
					</TableCell>
					<TableCell title={bean.avoidDiscounts ? "Factura de compensación de descuentos" : ""}
							   sx={{color: bean.avoidDiscounts ? "error.main" : undefined, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
						{bean.customer?.shortName || ""}
					</TableCell>
					<TableCell sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "text.secondary"}}
							   title={bean.description}>
						{bean.description}
					</TableCell>
					<TableCell sx={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
						{bean.organizationUnit?.name}
					</TableCell>
					<TableCell align="right" sx={{fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"}}>
						{bean.totals?.total ? euros(bean.totals.total) : ""}
					</TableCell>
				</>
			);
		},
		supplier: async (currentPage, size, term, showDismissed) => {
			// ⚠️ Los parámetros son POSICIONALES y el generador los ordena por nombre: al añadir
			// `overdue` al filtro del backend (#12844) se coló en sexta posición y desplazó a
			// `budgetId` y `customerId`. Si aquí se añade otro filtro, revisar esta llamada entera
			// en vez de fiarse de que compile.
			const from = dateFrom || undefined;
			const to = dateTo || undefined;
			// El total es de TODAS las que casan, no de la página: se pide aparte con los mismos filtros.
			const filtering = !!(term || from || to || listFilter.advanced || listFilter.pending
				|| listFilter.overdue || budgetId);
			setSearching(filtering);
			if (filtering) {
				apiManager
				.execute(ctrl.totalsBill(
					listFilter.advanced,
					listFilter.pending,
					listFilter.overdue,
					budgetId,
					undefined, // customerId
					from,
					to,
					term,
					showDismissed
				))
				.then(totals => setSearchTotals(totals))
				.catch(() => setSearchTotals(null));
			} else {
				setSearchTotals(null);
			}
			return ctrl.pageBill(
				currentPage,
				size,
				undefined,
				listFilter.advanced,
				listFilter.pending,
				listFilter.overdue,
				budgetId,
				undefined, // customerId (lo usa el dashboard de marketing; esta lista no)
				from,
				to,
				term,
				showDismissed
			);
		},
		toolbar: (
			<Stack spacing={2} sx={{mb: 2}}>
				<Stack direction={{xs: "column", md: "row"}} spacing={1.5} alignItems={{md: "center"}} justifyContent="space-between">
					<Stack direction="row" spacing={1} sx={{overflowX: "auto", pb: {xs: 0.5, md: 0}}}>
						<Chip clickable label="Todas" onClick={() => {
							setListFilter({pending: false, advanced: false, overdue: false});
							setTrigger(trigger + 1);
						}} color={noFilter ? "primary" : "default"} variant={noFilter ? "filled" : "outlined"}
							  sx={{fontWeight: 600, flexShrink: 0}}/>
						<Chip clickable onClick={() => toggle("pending")}
							  label={<>Pendientes {summary.pending ? <Box component="span" sx={{opacity: 0.75}}>· {euros(summary.pending)}</Box> : null}</>}
							  color={listFilter.pending ? "primary" : "default"} variant={listFilter.pending ? "filled" : "outlined"}
							  sx={{fontWeight: 600, flexShrink: 0}}/>
						<Chip clickable onClick={() => toggle("overdue")} label="Vencidas"
							  color={listFilter.overdue ? "primary" : "default"} variant={listFilter.overdue ? "filled" : "outlined"}
							  sx={{fontWeight: 600, flexShrink: 0}}/>
						<Chip clickable onClick={() => toggle("advanced")}
							  label={<>Adelantadas {summary.advanced ? <Box component="span" sx={{opacity: 0.75}}>· {euros(summary.advanced)}</Box> : null}</>}
							  color={listFilter.advanced ? "primary" : "default"} variant={listFilter.advanced ? "filled" : "outlined"}
							  sx={{fontWeight: 600, flexShrink: 0}}/>
					</Stack>
					<Stack direction="row" spacing={1.5}>
						<TextField label="Emitida desde" type="date" size="small" value={dateFrom}
								   sx={{width: {xs: "50%", md: 170}}}
								   onChange={(e) => {
									   setDateFrom(e.target.value);
									   setTrigger(trigger + 1);
								   }}
								   InputLabelProps={{shrink: true}}/>
						<TextField label="Emitida hasta" type="date" size="small" value={dateTo}
								   sx={{width: {xs: "50%", md: 170}}}
								   onChange={(e) => {
									   setDateTo(e.target.value);
									   setTrigger(trigger + 1);
								   }}
								   InputLabelProps={{shrink: true}}/>
					</Stack>
				</Stack>
				{searching && searchTotals && (
					// Cada bloque va entero: en pantallas estrechas salta de línea entre bloques, no dentro.
					<Box sx={{
						display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 0.5, px: 2, py: 1.25, borderRadius: 2,
						bgcolor: "grey.50", border: "1px solid", borderColor: "divider",
						"& > span": {whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"},
					}}>
						<Typography variant="body2" component="span" color="text.secondary">
							{searchTotals.count} {searchTotals.count === 1 ? "factura" : "facturas"}
						</Typography>
						<Typography variant="body2" component="span">Base {euros(searchTotals.subTotal)}</Typography>
						<Typography variant="body2" component="span">IVA {euros(searchTotals.taxes)}</Typography>
						<Typography variant="body2" component="span" fontWeight={700}>Total {euros(searchTotals.total)}</Typography>
					</Box>
				)}
			</Stack>
		),
		actions: [
			{
				action: () => {
					navigate("/app/admin/billing/bill-batch");
				},
				color: "primary",
				label: "Añadir en lote",
			},
		],
	};

	return (
		<>
			<AppList config={listConfig} trigger={trigger}/>
		</>
	);
};
